import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, STAGE_META, fmtMoney, fmtDate, formatError, isAdmin } from '../api';
import { useAuth } from '../AuthContext';
import { Search, Plus } from 'lucide-react';
import NewDealModal from '../components/NewDealModal';

export default function Leads() {
  const { user } = useAuth();
  const admin = isAdmin(user);
  const [deals, setDeals] = useState([]);
  const [meta, setMeta] = useState(null);
  const [filter, setFilter] = useState({ q: '', stage: '', contact_type: '' });
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [err, setErr] = useState('');

  const reload = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.q) params.set('q', filter.q);
      if (filter.stage) params.set('stage', filter.stage);
      const { data } = await api.get(`/deals?${params}`);
      const filtered = filter.contact_type ? data.filter((d) => d.contact_type === filter.contact_type) : data;
      setDeals(filtered);
    } catch (e) { setErr(formatError(e)); }
    setLoading(false);
  };

  useEffect(() => {
    api.get('/meta').then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [filter]);

  return (
    <div data-testid="crm-leads">
      <div className="crm-card" style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 220px', minWidth: 200 }}>
            <label className="crm-label">Search</label>
            <div style={{ position: 'relative' }}>
              <Search size={14} style={{ position: 'absolute', left: 12, top: 12, color: '#94a3b8' }} />
              <input
                className="crm-input"
                style={{ paddingLeft: 32 }}
                placeholder="Company, contact, project or rep…"
                value={filter.q}
                onChange={(e) => setFilter((f) => ({ ...f, q: e.target.value }))}
              />
            </div>
          </div>
          <div style={{ minWidth: 170 }}>
            <label className="crm-label">Stage</label>
            <select className="crm-select" value={filter.stage} onChange={(e) => setFilter((f) => ({ ...f, stage: e.target.value }))}>
              <option value="">All stages</option>
              {(meta?.stages || []).map((s) => <option key={s} value={s}>{STAGE_META[s]?.label || s}</option>)}
            </select>
          </div>
          <div style={{ minWidth: 150 }}>
            <label className="crm-label">Contractor type</label>
            <select className="crm-select" value={filter.contact_type} onChange={(e) => setFilter((f) => ({ ...f, contact_type: e.target.value }))}>
              <option value="">All types</option>
              {(meta?.contact_types || []).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <button className="crm-btn" onClick={() => setShowNew(true)} data-testid="leads-new-deal-btn"><Plus size={16} /> New Lead</button>
        </div>
      </div>

      {err && <div className="crm-card" style={{ color: '#9f1239', marginBottom: 12 }}>{err}</div>}

      <div className="crm-card" style={{ padding: 0, overflowX: 'auto' }}>
        <table className="crm-table" style={{ minWidth: 980 }}>
          <thead>
            <tr>
              <th>Company / Contact</th>
              <th>Type · Source</th>
              <th>Project</th>
              <th>Stage</th>
              <th style={{ textAlign: 'right' }}>Client Quote</th>
              {admin && <th style={{ textAlign: 'right' }}>Margin</th>}
              <th>Rep</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={admin ? 8 : 7} style={{ textAlign: 'center', color: 'var(--crm-muted)' }}>Loading…</td></tr>
              : deals.length === 0 ? <tr><td colSpan={admin ? 8 : 7} style={{ textAlign: 'center', color: 'var(--crm-muted)', padding: 30 }}>No deals match your filters. Add a new lead to get started.</td></tr>
                : deals.map((d) => {
                  const meta = STAGE_META[d.stage] || { label: d.stage, color: '#64748b', light: '#e2e8f0' };
                  const margin = Number(d.client_quote || 0) - Number(d.estimator_cost || 0);
                  return (
                    <tr key={d.id} className="row-link" onClick={() => { window.location.href = `/crm/deals/${d.id}`; }} data-testid={`leads-row-${d.id}`}>
                      <td>
                        <div style={{ fontWeight: 800 }}>{d.company}</div>
                        <div style={{ fontSize: 11.5, color: 'var(--crm-muted)' }}>{d.contact_name || '—'} · {d.contact_email || ''}</div>
                      </td>
                      <td><div>{d.contact_type}</div><div style={{ fontSize: 11.5, color: 'var(--crm-muted)' }}>{d.source}</div></td>
                      <td>{d.project_name || <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>Pending</span>}</td>
                      <td>
                        <span className="crm-pill" style={{ background: meta.light, color: meta.color }}>
                          <span className="dot" style={{ background: meta.color }} />{meta.label}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 700 }}>{d.client_quote > 0 ? fmtMoney(d.client_quote) : <span style={{ color: '#94a3b8' }}>—</span>}</td>
                      {admin && <td style={{ textAlign: 'right', color: margin > 0 ? 'var(--crm-emerald)' : 'var(--crm-muted)', fontWeight: 700 }}>{margin > 0 ? fmtMoney(margin) : '—'}</td>}
                      <td>{d.sales_rep_name || <span style={{ color: '#94a3b8' }}>—</span>}</td>
                      <td style={{ color: 'var(--crm-muted)' }}>{fmtDate(d.created_at)}</td>
                    </tr>
                  );
                })}
          </tbody>
        </table>
      </div>

      {showNew && <NewDealModal onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); reload(); }} />}
    </div>
  );
}
